/**
 * Study details page.
 */

import Alert from "@cloudscape-design/components/alert"
import Badge, { type BadgeProps } from "@cloudscape-design/components/badge"
import Box from "@cloudscape-design/components/box"
import Button from "@cloudscape-design/components/button"
import Container from "@cloudscape-design/components/container"
import Grid from "@cloudscape-design/components/grid"
import Header from "@cloudscape-design/components/header"
import Icon from "@cloudscape-design/components/icon"
import Spinner from "@cloudscape-design/components/spinner"
import { useEffect, useState, useMemo, useRef } from "react"
import { useParams, useNavigate, useSearchParams } from "react-router-dom"
import type { DTARecord } from "../../services/dtaApi"
import DTAUploadModal from "../../components/DTAUploadModal"
import Breadcrumb from "../../components/Navigation/Breadcrumb"
import { getDTADisplayName } from "../../components/shared/dtaDisplay"
import SharedTabs from "../../components/shared/SharedTabs"
import { useNotifications } from "../../contexts/NotificationContext"
import { StudyProtocolStatusEnum } from "../../generated/sdk"
import { useCreateDta, useUploadDocument } from "../../hooks/queries/useDtas"
import { useProtocolDetail } from "../../hooks/queries/useProtocol"
import { useStudyDetail } from "../../hooks/queries/useStudyDetail"
import {
  useStudyDtasList,
  useStudyRuns,
  useStudyMetrics,
  useAmendDta,
} from "../../hooks/queries/useStudyDtas"
import AmendDtaModal from "./components/AmendDtaModal"
import DataStreamHistory from "./components/DataStreamHistory"
import DtaTable from "./components/DtaTable"
import IngestedDataPanel from "./IngestedDataPanel"
import { ProtocolPanel } from "./Protocol"
import "./StudyDetails.css"
import RunHistoryPanel from "./RunHistoryPanel"
import StudyAccessPanel from "./StudyAccessPanel"
import StudyHealthPanel from "./StudyHealthPanel"

const statusBadgeColor = (status: string): BadgeProps["color"] => {
  if (status === "APPROVED" || status === "ACTIVE") return "green"
  if (status === "REVIEW") return "blue"
  return "grey"
}

const MetricSkeleton = () => (
  <Container>
    <Box textAlign="center" padding="s" color="text-status-inactive">
      <Spinner />
    </Box>
  </Container>
)

const StudyDetails = () => {
  const { studyId } = useParams<{ studyId: string }>()
  const navigate = useNavigate()
  const { showToast } = useNotifications()
  const [searchParams] = useSearchParams()

  const [showUploadModal, setShowUploadModal] = useState(false)
  const [amendTarget, setAmendTarget] = useState<DTARecord | null>(null)

  // Publish the header height as --study-header-height so the sticky tabs pin beneath it. A
  // callback ref re-attaches the observer because the header mounts in both loading and loaded states.
  const headerObserver = useRef<ResizeObserver | null>(null)
  const measureHeader = (header: HTMLElement | null) => {
    headerObserver.current?.disconnect()
    if (!header) return
    const page = header.parentElement
    if (!page) return
    headerObserver.current = new ResizeObserver(() => {
      page.style.setProperty("--study-header-height", `${header.offsetHeight}px`)
    })
    headerObserver.current.observe(header)
  }
  useEffect(() => () => headerObserver.current?.disconnect(), [])

  const { data: study, isLoading: loading, error: studyError, refetch } = useStudyDetail(studyId)
  const { data: protocol } = useProtocolDetail(studyId)
  const protocolApproved = protocol?.status === StudyProtocolStatusEnum.Approved

  // Open the upload modal on ?action=create, but only once the protocol is approved.
  useEffect(() => {
    if (searchParams.get("action") === "create") {
      if (protocolApproved) {
        setShowUploadModal(true)
      } else if (protocol !== undefined) {
        showToast({
          subject: "Cannot create Data Transfer Agreement",
          message: "An approved protocol is required before creating Data Transfer Agreements.",
          variant: "warning",
        })
      }
    }
  }, [searchParams, protocolApproved, protocol, showToast])

  const { data: runsData } = useStudyRuns(study ? studyId : undefined)
  const {
    data: dtaPages,
    isLoading: loadingDtas,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useStudyDtasList(study ? studyId : undefined)

  // Fetch all pages for client-side filtering.
  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage) {
      void fetchNextPage()
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  const allDtas = useMemo(() => dtaPages?.pages.flatMap((p) => p.items) ?? [], [dtaPages])

  const metrics = useStudyMetrics(allDtas, runsData)

  const uploadDocument = useUploadDocument(studyId ?? "")
  const createDta = useCreateDta(studyId ?? "")

  const amendDta = useAmendDta(studyId)

  const handleAmendConfirm = (changeReason: string) => {
    if (!amendTarget) return
    amendDta.mutate(
      { dtaId: amendTarget.dta_id, changeReason },
      {
        onSuccess: (newVersion) => {
          setAmendTarget(null)
          showToast({
            subject: "Amendment created",
            message: `Version ${newVersion.version} created as a draft.`,
            variant: "success",
          })
          // Open the new draft version so the DAE can edit the amendment right away.
          navigate(`/dtas/${amendTarget.dta_id}/v/${newVersion.version}`)
        },
        onError: (err) => {
          showToast({
            subject: "Amendment failed",
            message: err instanceof Error ? err.message : String(err),
            variant: "error",
          })
        },
      },
    )
  }

  const handleDocumentUpload = async (file: File, vendorId: string, vendorName: string) => {
    if (!studyId) return
    const { upload_id: uploadId } = await uploadDocument.mutateAsync({ file, uploadType: "dta" })
    const result = await createDta.mutateAsync({
      vendor_id: vendorId,
      vendor_name: vendorName,
      domains: [],
      source_type: "third-party",
      upload_id: uploadId,
    })
    showToast({
      subject: "Data Transfer Agreement Created",
      message: "Extraction is in progress. You'll see the content once it completes.",
      variant: "success",
    })
    navigate(`/dtas/${result.dta_id}`)
  }

  const formatDate = (d: string | Date) => {
    try {
      return new Date(d).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      })
    } catch {
      return String(d)
    }
  }

  const error = studyError
    ? studyError instanceof Error
      ? studyError.message
      : "Failed to load study"
    : null

  if (error === "UNAUTHORIZED") {
    navigate("/login")
    return null
  }

  if (loading)
    return (
      <div className="study-page">
        <Breadcrumb
          items={[{ label: "All Studies", path: "/studies" }, { label: studyId || "" }]}
        />
        <Box textAlign="center" padding={{ vertical: "xxxl" }} color="text-status-inactive">
          <Spinner size="large" /> Loading study…
        </Box>
      </div>
    )

  if (error || !study)
    return (
      <div className="study-page">
        <Breadcrumb
          items={[{ label: "All Studies", path: "/studies" }, { label: studyId || "" }]}
        />
        <Box padding="l">
          <Alert
            type="error"
            header="Error"
            action={<Button onClick={() => void refetch()}>Try Again</Button>}
          >
            {error || "Study not found"}
          </Alert>
        </Box>
      </div>
    )

  return (
    <div className="study-page">
      <Breadcrumb items={[{ label: "All Studies", path: "/studies" }, { label: study.study_id }]} />

      <header className="study-header" ref={measureHeader}>
        <div className="study-header-main">
          <Grid gridDefinition={[{ colspan: 10 }, { colspan: 2 }]}>
            <Header
              variant="h1"
              description={[
                study.study_id,
                study.created_by,
                `Created ${formatDate(study.creation_date)}`,
                study.therapeutic_area,
                study.indication,
              ]
                .filter(Boolean)
                .join("  •  ")}
            >
              {study.study_description || study.study_name || study.study_id}
            </Header>
            <Box textAlign="right">
              <Badge color={statusBadgeColor(study.study_status)}>
                {study.study_status.replace(/_/g, " ")}
              </Badge>
            </Box>
          </Grid>
        </div>
      </header>

      <main className="study-tabs-band">
        <SharedTabs
          ariaLabel="Study sections"
          scrollToTopOnChange
          tabs={[
            {
              id: "overview",
              label: (
                <>
                  <Icon name="view-horizontal" size="normal" /> Overview
                </>
              ),
              content: (
                <div className="study-overview">
                  {metrics ? (
                    <StudyHealthPanel
                      studyId={study.study_id}
                      metrics={metrics}
                      loading={loadingDtas}
                    />
                  ) : (
                    <MetricSkeleton />
                  )}

                  <DtaTable
                    dtas={allDtas}
                    loading={loadingDtas}
                    totalCount={allDtas.length}
                    fetchingMore={isFetchingNextPage}
                    onAmend={setAmendTarget}
                    actions={
                      <Button
                        variant="primary"
                        disabled={!protocolApproved}
                        disabledReason="An approved protocol is required before creating DTAs"
                        onClick={() => setShowUploadModal(true)}
                      >
                        Create DTA
                      </Button>
                    }
                  />
                </div>
              ),
            },
            {
              id: "dtas",
              label: (
                <>
                  <Icon name="file" size="normal" /> DTAs
                </>
              ),
              content: (
                <div className="study-dtas">
                  <DtaTable
                    dtas={allDtas}
                    loading={loadingDtas}
                    totalCount={allDtas.length}
                    fetchingMore={isFetchingNextPage}
                    onAmend={setAmendTarget}
                    actions={
                      <Button
                        variant="primary"
                        disabled={!protocolApproved}
                        disabledReason="An approved protocol is required before creating DTAs"
                        onClick={() => setShowUploadModal(true)}
                      >
                        Create DTA
                      </Button>
                    }
                  />

                  <DataStreamHistory studyId={study.study_id} />
                </div>
              ),
            },
            {
              id: "protocol",
              label: (
                <>
                  <Icon name="list-view" size="normal" /> Protocol
                </>
              ),
              content: (
                <div>
                  <ProtocolPanel studyId={study.study_id} />
                </div>
              ),
            },
            {
              id: "ingestion",
              label: (
                <>
                  <Icon name="download" size="normal" /> Ingestion
                </>
              ),
              content: (
                <div className="study-ingestion">
                  <RunHistoryPanel studyId={study.study_id} />
                  <IngestedDataPanel studyId={study.study_id} />
                </div>
              ),
            },
            {
              id: "access",
              label: (
                <>
                  <Icon name="lock-private" size="normal" /> Access
                </>
              ),
              content: <StudyAccessPanel studyId={study.study_id} />,
            },
          ]}
        />
      </main>

      {showUploadModal && studyId && (
        <DTAUploadModal
          studyId={studyId}
          onClose={() => setShowUploadModal(false)}
          onComplete={() => void refetch()}
          onDocumentUpload={handleDocumentUpload}
        />
      )}

      {amendTarget && (
        <AmendDtaModal
          dtaName={getDTADisplayName(amendTarget)}
          loading={amendDta.isPending}
          onConfirm={handleAmendConfirm}
          onClose={() => setAmendTarget(null)}
        />
      )}
    </div>
  )
}

export default StudyDetails
